import { inject, Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { DeliveryNoteBundlesV3Service } from 'api-v3/services';
import { SortType, DeliveryNoteBundlesFilterProperties } from 'api-v3/models';
import { UserRoles } from 'api/models';
import { FoldersStore, FolderData } from '@app/stores/foldersStore';
import { SearchStore } from '@app/stores/searchStore';
import { AppStore } from '@app/stores/appStore';
import { ABORT_SIGNAL } from '@app/shared/constants/http-context-tokens';

@Injectable({
    providedIn: 'root',
})
export class ConsignorFoldersPaginatedService {
    private foldersStore = inject(FoldersStore);
    private searchStore = inject(SearchStore);
    private appStore = inject(AppStore);

    constructor(
        private deliveryNoteBundlesV3Service: DeliveryNoteBundlesV3Service
    ) {}

    async loadConsignorFoldersPaginated(
        organizationSiteKey: string
    ): Promise<void> {
        // Prevent duplicate calls if already loading
        if (this.foldersStore.loading()) {
            return;
        }

        this.foldersStore.setFoldersLoading(true);
        this.foldersStore.setFoldersError(null);

        // Cancel any ongoing requests and get new abort signal
        const abortSignal = this.searchStore.cancelOngoingRequests();

        try {
            const searchText = this.searchStore.searchText();
            const pagination = this.searchStore.pagination();
            const sort = this.searchStore.sort();
            const selectedFilterKey = this.searchStore.selectedFilterKey();

            // Get site information for role-based read_only logic, selected locations, and showClosed filter
            const currentSite = this.appStore.currentOrganizationSite();
            const selectedSiteKeys = this.appStore.getSelectedLocationKeys('consignor');
            const showClosed = this.appStore.showClosed();

            // Don't make API call if no organization sites are selected
            if (selectedSiteKeys.length === 0) {
                return;
            }

            // Check if request was cancelled before proceeding
            if (abortSignal?.aborted) {
                return;
            }

            const sortBy = sort.sortBy;

            // Use valid sort field or fall back to default - ensure type safety
            const validSortBy = sortBy as any;

            // Default to desc for dateCreated (newest first), use user's direction for other fields
            const sortDirection =
                validSortBy === 'dateCreated' && !sortBy
                    ? SortType.Desc // Default to newest first when no sort is specified
                    : sort.sortDirection === 'asc'
                      ? SortType.Asc
                      : SortType.Desc;

            // Handle uppercase conversion for specific filter properties
            let filterValue = searchText || undefined;
            const filterProperty =
                selectedFilterKey?.keyName;

            if (
                filterValue &&
                filterProperty &&
                (filterProperty === 'bundleNumber' ||
                    filterProperty === 'tourNumber')
            ) {
                filterValue = filterValue.toUpperCase();
            }

            const params = {
                organizationSiteKey,
                organizationSiteKeys:
                    selectedSiteKeys.length > 0 ? selectedSiteKeys : undefined,
                limit: pagination.limit,
                offset: pagination.offset,
                sort: sortDirection,
                sortBy: validSortBy,
                filterProperty: filterProperty
                    ? (filterProperty as DeliveryNoteBundlesFilterProperties)
                    : undefined,
                filterValue: filterValue,
                filterNotClosed: !showClosed,
            };

            // Check if request was already cancelled before making API call
            if (abortSignal?.aborted) {
                return;
            }

            // Create HttpContext with abort signal for proper HTTP request cancellation
            const context = new HttpContext().set(ABORT_SIGNAL, abortSignal);

            // This will go through the HTTP interceptors and can be cancelled
            const response = await lastValueFrom(
                this.deliveryNoteBundlesV3Service.getConsignorDeliveryNoteBundlesV3(
                    params,
                    context
                )
            );

            if (response && response.data) {
                // Map the response to include orgSiteKey and ensure proper typing
                const foldersWithOrgSiteKey: FolderData[] = response.data.map(
                    bundle => ({
                        ...bundle,
                        orgSiteKey: organizationSiteKey,
                        read_only: currentSite
                            ? currentSite.roles.includes(
                                  UserRoles.RemoteConsignor
                              ) &&
                              !currentSite.roles.includes(UserRoles.Consignor)
                            : false,
                    })
                );

                this.foldersStore.setFolders(foldersWithOrgSiteKey);

                // Update pagination info in centralized searchStore
                this.searchStore.updatePaginationFromApiResponse(response.paginationInfo);
            }
        } catch (error: any) {
            // Handle cancelled requests gracefully
            if (
                error?.name === 'AbortError' ||
                error?.message === 'Subscription has been cancelled' ||
                abortSignal?.aborted
            ) {
                return;
            }

            // Extract request ID from error if available
            const requestId =
                error?.headers?.get?.('x-request-id') ||
                error?.error?.requestId ||
                null;
            const errorMessage = requestId
                ? `GENERAL.ERRORS.FAILED_TO_LOAD_CONSIGNOR_FOLDERS|${requestId}`
                : 'GENERAL.ERRORS.FAILED_TO_LOAD_CONSIGNOR_FOLDERS';

            this.foldersStore.clearFolders();
            this.foldersStore.setFoldersError(errorMessage);

            // Reset pagination to empty state on error
            this.searchStore.resetPaginationToEmpty();
        } finally {
            // Only set loading to false if request wasn't cancelled
            if (!abortSignal?.aborted) {
                this.foldersStore.setFoldersLoading(false);
            }
        }
    }
}
